import { useGetLabelingCoverage } from '@/hooks/api/assets/useGetLabelingCoverage';
import { useGetLatestDuplicationReport } from '@/hooks/api/reports/useGetLatestDuplicationReport';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useTenants } from '@/services/tenants/useTenants';
import { Recommendation, recommendationCatalogue, selectRecommendations } from './recommendationCatalogue';

export type RecommendationCard = {
  id: string;
  title: string;
  pitch: string;
  callToAction: { label: string; to: string };
};

export const useRecommendations = ({
  assetsTotal,
  catalogue = recommendationCatalogue,
}: {
  assetsTotal: number;
  catalogue?: Recommendation[];
}): { recommendations: RecommendationCard[]; isLoading: boolean } => {
  const { getPath } = useTenants();
  const { isTaxonomyEnabled, isPartFinderEnabled } = useFeatureFlags();
  const { permissions } = useGetCurrentUser();
  const labelingCoverage = useGetLabelingCoverage({ enabled: isTaxonomyEnabled });
  const latestDuplicationReport = useGetLatestDuplicationReport();

  const isLoading = labelingCoverage.isLoading || latestDuplicationReport.isLoading;
  const coverage = labelingCoverage.data;
  const signals = {
    isTaxonomyEnabled,
    isPartFinderEnabled,
    canManageTaxonomy: permissions.canManageTaxonomy,
    assetsTotal,
    unlabeledAssets: coverage ? coverage.totalAssets - coverage.labeledAssets : 0,
    hasCompletedDuplicationReport: latestDuplicationReport.isError || !!latestDuplicationReport.data,
  };

  const recommendations = selectRecommendations(catalogue, signals).map(entry => ({
    id: entry.id,
    title: entry.title,
    pitch: entry.pitch(signals),
    callToAction: { label: entry.callToAction.label, to: getPath(entry.callToAction.path) },
  }));

  return { recommendations, isLoading };
};
